/**
 * Transpose the raw nowcast rows into the per-percentile arrays Plotly wants.
 *
 * The backend emits two shapes and we handle both:
 *   1. Wide rows with `p5, p16, p50, p84, p95, trend` columns.
 *   2. Long rows with `{ concept, val }` pairs (one row per percentile).
 */
import type { NowcastRow, ProcessedNowcast } from '../types';

export function processNowcastData(rows: NowcastRow[]): ProcessedNowcast {
  const dateData: Record<string, Record<string, number | null | undefined>> = {};

  for (const row of rows) {
    const dateStr = row.obs_date ?? row.date ?? row.Date;
    if (!dateStr) continue;

    const bucket = dateData[dateStr] ?? (dateData[dateStr] = {});

    if (row.concept && row.val !== undefined) {
      bucket[row.concept] = row.val ?? null;
      continue;
    }

    for (const col of ['p5', 'p16', 'p50', 'p84', 'p95', 'trend'] as const) {
      const value = row[col];
      if (value !== undefined) {
        bucket[col] = value;
      }
    }
  }

  const dates = Object.keys(dateData).sort();
  const result: ProcessedNowcast = {
    dates,
    p5: [],
    p16: [],
    p50: [],
    p84: [],
    p95: [],
    trend: [],
  };

  for (const date of dates) {
    const day = dateData[date];
    result.p5.push(numberOrNull(day?.p5));
    result.p16.push(numberOrNull(day?.p16));
    result.p50.push(numberOrNull(day?.p50));
    // fan-plot fallback: mirror p16 if backend only returns one inner band.
    result.p84.push(numberOrNull(day?.p84 ?? day?.p16));
    result.p95.push(numberOrNull(day?.p95));
    result.trend.push(numberOrNull(day?.trend));
  }

  return result;
}

function numberOrNull(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  const num = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(num) ? num : null;
}
